import { Info, X } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { StoryRow, RankedList } from '../page-blocks/implementations'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import {
  EDITORIAL_GRID_PRESET_CONFIG,
  type EditorialGridLayoutPreset,
} from '../page-blocks/definitions/editorialGrid'
import { PLACEHOLDER_ARTICLES } from '../editor-shared/placeholder-data'
import type { CanvasSlot } from '../editor-shared/canvasTypes'
import { COL_KEYS } from '../editor-shared/canvasTypes'
import { DarkModeToggle } from '../DarkModeToggle'

// ─── Spacer sizes ─────────────────────────────────────────────────────────────

const SPACER_PX: Record<string, number> = { sm: 8, md: 24, lg: 48, xl: 80 }

function formatDate(iso: string | undefined): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch { return iso ?? '' }
}

// ─── Per-slot live renderer ───────────────────────────────────────────────────

function PreviewSlot({ slot, compact = false, imagePadding = false, offset = 0 }: { slot: CanvasSlot; compact?: boolean; imagePadding?: boolean; offset?: number }) {
  // Distribute the article pool across slots so each block gets a distinct
  // window into the same pool (wraps around if needed).
  function articlesAt(start: number, count: number) {
    return Array.from({ length: count }, (_, i) =>
      PLACEHOLDER_ARTICLES[(offset + start + i) % PLACEHOLDER_ARTICLES.length]
    )
  }
  const article0 = PLACEHOLDER_ARTICLES[offset % PLACEHOLDER_ARTICLES.length]

  switch (slot.type) {
    case 'story-row': {
      const cfg = slot.config
      const rawCount = Number(cfg.columnCount ?? 3)
      const columnCount = ([2, 3, 4, 5].includes(rawCount) ? rawCount : 3) as 2 | 3 | 4 | 5
      return (
        <StoryRow
          articles={articlesAt(0, columnCount)}
          config={{
            columnCount,
            imageAspectRatio: (cfg.imageAspectRatio as '16/9' | '4/3' | '1/1' | '3/4') ?? '4/3',
            sectionHeaderEnabled: Boolean(cfg.sectionHeaderEnabled),
            sectionHeaderTitle: (cfg.sectionHeaderTitle as string) ?? null,
            sectionHeaderStyle: (cfg.sectionHeaderStyle as 'rule-left' | 'all-caps' | 'plain') ?? 'rule-left',
            showImages: cfg.showImages !== false,
            showSummary: Boolean(cfg.showSummary),
          }}
        />
      )
    }

    case 'ranked-list': {
      const cfg = slot.config
      return (
        <RankedList
          articles={articlesAt(0, 5)}
          config={{
            sectionHeaderEnabled: Boolean(cfg.sectionHeaderEnabled),
            sectionHeaderTitle: (cfg.sectionHeaderTitle as string) ?? null,
            sectionHeaderStyle: (cfg.sectionHeaderStyle as 'rule-left' | 'all-caps' | 'plain') ?? 'rule-left',
            numberingStyle: (cfg.numberingStyle as 'numeric' | 'pill' | 'minimal') ?? 'numeric',
            showSummary: Boolean(cfg.showSummary),
            showFooterAction: Boolean(cfg.showFooterAction),
            footerActionLabel: (cfg.footerActionLabel as string) ?? 'Explore More',
          }}
        />
      )
    }

    case 'unranked-list': {
      const articles = articlesAt(0, 8)
      return (
        <div>
          {articles.map((article, i) => (
            <div key={i} className="px-3 py-2.5">
              <div className="font-zodiak text-xs font-bold leading-snug text-fg">
                ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}
              </div>
              <div className="mt-1 text-[9px] uppercase tracking-wide text-fg-secondary">
                {formatDate(article.publishedAt ?? article.updatedAt)} · {article.regions?.[0]}
              </div>
            </div>
          ))}
        </div>
      )
    }

    case 'inline-feature': {
      const article = article0
      const titleSize = compact ? 'text-sm' : 'text-3xl'
      const gridCols = compact ? 'grid-cols-[1fr_1fr]' : 'grid-cols-[2fr_3fr]'
      const padding = compact ? 'p-4' : 'p-6 pr-8'
      const aspectRatio = compact ? 'aspect-[4/3]' : 'aspect-[2/1]'
      return (
        <div className={`grid ${gridCols} overflow-hidden`}>
          {/* Text column — stretches to match image height */}
          <div className={`flex flex-col ${padding}`}>
            <h3 className={`font-zodiak ${titleSize} font-bold leading-tight text-fg`}>
              ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}
            </h3>
            <p className={`mt-3 text-fg-secondary ${compact ? 'text-xs leading-4' : 'text-sm leading-6'}`}>{article.description}</p>
            <div className="mt-auto pt-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">{article.regions?.[0]}</div>
              <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-fg-secondary">
                {formatDate(article.publishedAt ?? article.updatedAt)}
                <span aria-hidden="true">·</span>
                <span>&#x1F4F7;</span>
                <span>{article.thumbnail?.classification ? [article.thumbnail.classification, ...(article.thumbnail.disseminationControls ?? [])].join('//') : [article.classification, ...(article.disseminationControls ?? [])].join('//')}</span>
              </div>
            </div>
          </div>
          {/* Image column — aspect ratio owns the height */}
          <div className={`${aspectRatio} ${imagePadding ? 'pr-3' : ''}`}>
            {article.thumbnail?.presignedUrl ? (
              <img src={article.thumbnail.presignedUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center bg-fill text-fg-secondary">No image</div>
            )}
          </div>
        </div>
      )
    }

    case 'feature-with-rail': {
      const lead = PLACEHOLDER_ARTICLES[1]
      const supporting = articlesAt(2, 3)
      return (
        <div className="grid grid-cols-[2fr_1fr_1fr] divide-x divide-line overflow-hidden">
          <div className="p-5">
            {lead.thumbnail?.presignedUrl && (
              <img src={lead.thumbnail.presignedUrl} alt="" className="mb-3 aspect-video w-full object-cover" />
            )}
            <h3 className="font-zodiak text-xl font-bold leading-tight text-fg">
              ({[lead.classification, ...(lead.disseminationControls ?? [])].join('//')}) {lead.title}
            </h3>
            <p className="mt-2 text-sm leading-6 text-fg-secondary">{lead.description}</p>
            <div className="mt-3 text-[10px] uppercase tracking-wide text-fg-secondary">{formatDate(lead.publishedAt)}</div>
          </div>
          <div className="col-span-2 divide-y divide-line">
            {supporting.map((art, i) => (
              <div key={i} className="flex gap-3 p-3">
                {art.thumbnail?.presignedUrl && (
                  <img src={art.thumbnail.presignedUrl} alt="" className="h-16 w-16 shrink-0 object-cover" />
                )}
                <div>
                  <div className="font-zodiak text-xs font-bold leading-snug text-fg">
                    ({[art.classification, ...(art.disseminationControls ?? [])].join('//')}) {art.title}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-wide text-fg-secondary">{art.regions?.[0]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    case 'hero-overlay': {
      const lead = article0
      const secondary = articlesAt(3, 2)
      return (
        <div className="relative overflow-hidden rounded border border-line" style={{ minHeight: '300px' }}>
          {lead.thumbnail?.presignedUrl ? (
            <img src={lead.thumbnail.presignedUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gray-400" />
          )}
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative grid h-full grid-cols-[3fr_1fr]" style={{ minHeight: '300px' }}>
            <div className="flex flex-col justify-end p-6">
              <div className="mb-2">
                <span className="bg-red-9 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                  Top News
                </span>
              </div>
              <h3 className="font-zodiak text-3xl font-bold leading-tight text-white">
                ({[lead.classification, ...(lead.disseminationControls ?? [])].join('//')}) {lead.title}
              </h3>
              <p className="mt-2 text-sm text-white/70">{lead.description}</p>
              <div className="mt-3 text-[9px] uppercase tracking-wide text-white/50">
                {lead.regions?.[0]} · {formatDate(lead.publishedAt ?? lead.updatedAt)}
              </div>
            </div>
            <div className="flex flex-col justify-center gap-4 border-l border-white/20 bg-black/40 p-4">
              {secondary.map((art, i) => (
                <div key={i} className="border-b border-white/20 pb-3 last:border-0 last:pb-0">
                  <div className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-white/50">
                    {art.regions?.[0]}
                  </div>
                  <div className="font-zodiak text-xs font-bold leading-snug text-white">
                    ({[art.classification, ...(art.disseminationControls ?? [])].join('//')}) {art.title}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }

    case 'livewire-callout': {
      const article = article0
      return (
        <div className="flex items-center gap-4 rounded border border-line bg-fill px-4 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-solid text-sm text-white">
            ▶
          </div>
          <div className="flex-1">
            <div className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Live Now</div>
            <div className="font-zodiak text-base font-bold text-fg">
              ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}
            </div>
          </div>
          <div className="text-xs text-fg-secondary">24 min</div>
        </div>
      )
    }

    case 'editorial-grid': {
      const preset = (slot.config.layoutPreset as EditorialGridLayoutPreset) ?? '3-equal'
      const presetCfg = EDITORIAL_GRID_PRESET_CONFIG[preset]
      const colKeys = COL_KEYS.slice(0, presetCfg.columns)
      const showDividers = slot.config.showDividers !== false
      return (
        <div
          className={`overflow-hidden ${showDividers ? 'divide-x divide-line' : ''}`}
          style={{ display: 'grid', gridTemplateColumns: presetCfg.gridTemplate }}
        >
          {colKeys.map((colKey, i) => {
            const colBlocks = slot.columnBlocks?.[colKey] ?? []
            const isLastCol = i === colKeys.length - 1
            if (colBlocks.length === 0) {
              return (
                <div
                  key={colKey}
                  className="flex items-center justify-center p-6 text-xs text-fg-secondary opacity-50"
                >
                  Empty column
                </div>
              )
            }
            return (
              <div key={colKey} className="min-w-0 divide-y divide-line overflow-hidden">
                {colBlocks.map((colBlock, bi) => {
                  const colDef = PAGE_BLOCK_REGISTRY[colBlock.type]
                  return (
                    <PreviewSlot
                      key={colBlock.id}
                      compact
                      imagePadding={showDividers && !isLastCol}
                      offset={offset + i + bi + 1}
                      slot={{
                        id: `${slot.id}-${colKey}-${bi}`,
                        type: colBlock.type,
                        order: bi,
                        config: { ...(colDef?.defaultConfig ?? {}) },
                      }}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>
      )
    }

    case 'section-divider': {
      const style = (slot.config.style as string) ?? 'plain'
      const label = slot.config.label as string | null
      return (
        <div className="flex items-center gap-3 py-2">
          {style === 'labeled' && label ? (
            <>
              <div className="flex-1 border-t border-line" />
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{label}</span>
              <div className="flex-1 border-t border-line" />
            </>
          ) : (
            <div
              className={`w-full border-t ${style === 'thick' ? 'border-t-2 border-fg-secondary opacity-40' : 'border-line'}`}
            />
          )}
        </div>
      )
    }

    case 'spacer': {
      const size = (slot.config.size as string) ?? 'md'
      const px = SPACER_PX[size] ?? 24
      return <div style={{ height: px }} />
    }

    default:
      return null
  }
}

// ─── LayoutPreview ────────────────────────────────────────────────────────────────

interface LayoutPreviewProps {
  slots: CanvasSlot[]
  /** 'overlay' (default) — rendered inside a Dialog, includes a Dialog.Close button.
   *  'popout' — rendered as a standalone full page in its own window.
   *  'inline' — renders only the page canvas, no toolbar or scroll wrapper (parent provides both). */
  mode?: 'overlay' | 'popout' | 'inline'
}

export function LayoutPreview({ slots, mode = 'overlay' }: LayoutPreviewProps) {
  const isPopout = mode === 'popout'
  const isInline = mode === 'inline'

  const canvas = (
    <div className="mx-auto max-w-7xl px-6 py-8">

      {/* Disclaimer banner */}
      <div className="mb-6 flex items-start gap-3 rounded border border-warning-line bg-warning-fill px-4 py-3 text-xs text-warning-fg">
        <Info size={14} className="mt-0.5 shrink-0 text-amber-9" aria-hidden="true" />
        <p>
          <span className="font-semibold">Preview is structural only.</span>{' '}
          Page-level decisions — such as how many articles populate a variable-length list, or section headers like "Today's Top Articles" — are not reflected here.
          Vertical spacing and block heights may differ significantly from what appears on a published page.
        </p>
      </div>

      {slots.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-sm text-fg-secondary">
          No rows in layout yet.
        </div>
      ) : (
        <div className="space-y-10">
          {slots.map((slot, i) => (
            <PreviewSlot key={slot.id} slot={slot} offset={i} />
          ))}
        </div>
      )}
    </div>
  )

  if (isInline) return canvas

  return (
    <div className={isPopout ? 'flex h-screen flex-col bg-canvas' : 'fixed inset-0 z-50 flex flex-col bg-canvas'}>

      {/* Preview toolbar */}
      <div className="flex shrink-0 items-center justify-between border-b border-line bg-accent-tertiary-fill px-6 py-2.5">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest text-fg-secondary">Layout Preview</span>
          <span className="rounded-full bg-fill px-2 py-0.5 text-[10px] font-semibold text-fg-secondary">
            {slots.length} row{slots.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <DarkModeToggle className="flex h-7 w-7 items-center justify-center rounded border border-line bg-canvas text-fg transition-colors hover:bg-fill" />
          {isPopout ? (
            <button
              type="button"
              onClick={() => window.close()}
              className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
            >
              <X size={12} aria-hidden="true" />
              Close Window
            </button>
          ) : (
            <Dialog.Close asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
              >
                <X size={12} aria-hidden="true" />
                Close Preview
              </button>
            </Dialog.Close>
          )}
        </div>
      </div>

      {/* Scrollable page canvas */}
      <div className="flex-1 overflow-y-auto">
        {canvas}
      </div>
    </div>
  )
}
